import { useRef } from 'react'

import { Stack } from '@mantine/core'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { format } from 'date-fns'
import dayjs from 'dayjs'

import { NavChart, type NavChartRef } from '@/components/NavChart'
import { NavPageFloatingHeader } from '@/components/NavPageFloatingHeader'
import { withTitle } from '@/utils/withTitle'

type NAVPageSearchParams = {
  date?: string
  from?: string
  to?: string
}

export const Route = createFileRoute('/_app/fund/$id/nav')({
  component: NAVPage,
  validateSearch: (
    search?: Record<'date' | 'from' | 'to', string>,
  ): NAVPageSearchParams => {
    const date =
      typeof search?.date === 'string'
        ? search.date
        : format(new Date(), 'yyyy-MM-dd')
    const from =
      typeof search?.from === 'string'
        ? search.from
        : dayjs().subtract(1, 'month').format('YYYY-MM-DD')
    const to =
      typeof search?.to === 'string' ? search.to : dayjs().format('YYYY-MM-DD')
    return {
      date,
      from,
      to,
    }
  },
  head: withTitle((params) => `Fund ${params.id!.toUpperCase()} - NAV`),
})

function NAVPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate({ from: Route.fullPath })
  const { date: selectedDate, from: startDate, to: endDate } = Route.useSearch()
  const navChartRef = useRef<NavChartRef>(null)

  const setSelectedDate = (newDate: string) => {
    void navigate({
      search: { date: newDate, from: startDate, to: endDate },
      replace: true,
    })
  }

  const handleRangeChange = (newStartDate: string, newEndDate: string) => {
    void navigate({
      search: { from: newStartDate, to: newEndDate, date: selectedDate },
      replace: true,
    })
  }

  return (
    <Stack>
      <NavPageFloatingHeader
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        startDate={startDate}
        endDate={endDate}
        onRangeChange={handleRangeChange}
      />
      <NavChart
        ref={navChartRef}
        fundId={id!}
        onSelectDate={setSelectedDate}
        selectedDate={selectedDate}
        startDate={startDate}
        endDate={endDate}
        onRangeChange={handleRangeChange}
      />
    </Stack>
  )
}
